/**
 * Pure unit-economics model for the Phase 0 standalone pricing calculator
 * (plan of record: modules/pricing-experiments/plans/2026-08-31-pricing-loop-plan-v2.md §0).
 *
 * Chain semantics (binding, settled 2026-08-31):
 *   A  — acquisition: cps → p1 (first→second) → p2 (→paywall)
 *   A2 — paywall → pressed buy (p3)
 *   B  — paid-of-pressed: the MENU distributes PRESSED users over offers + no-buy
 *        via a nested-logit choice model on per-offer SOLO anchors. Solo anchors
 *        are paid|pressed (price_id fills on footer press) — never divide by a
 *        pay rate again.
 *   C  — mix of payers (derived from B takes)
 *   D  — in-product economics: checks-per-subscriber is the universal knob.
 *
 * This module is pure: no React, no Firebase, no network. Every number the UI
 * shows is computed here so the unit tests pin the arithmetic (AC-004..006,
 * AC-010 in 2026-08-31-pricing-loop-v2-acceptance.md).
 *
 * Moved here from `src/modules/funnel-insights/pricingCalculator.ts` (2026-09-17)
 * so the public unit-economics calculator and the admin one run the SAME math.
 * The shipped half below is frozen: 18 files import it through a re-export shim,
 * so names, signatures and numbers never change here. Everything under
 * «product-shaped model» is the additive half the public widget runs on
 * (owner rulings R1/R2, 2026-09-17) — it never rewrites the shipped half.
 *
 * Pure: imports only its own siblings in this directory.
 */

import {
  CHECK_DEFAULT_BY_FAMILY,
  familyOf,
  trialDaysOf,
  trialOf,
  type OfferFamily,
  type UnitProduct,
} from './product';
import {
  basisValue,
  chargedAmount,
  disputeLossOnCharge,
  emptyDeductions,
  exTaxAmount,
  firstChargeOf,
  hasDeductions,
  netCharge,
  netCharges,
  productValueWith,
  refundLossOnCharge,
  type ChargeReader,
  type CustomFee,
  type DeductedTotals,
  type Deductions,
} from './deductions';

/**
 * Claim class of a price identity (AC-002/003): `observed` = provider truth
 * (paywallProducts + Stripe), `scenario` = derived/benchmark, `unavailable` =
 * typed absence (Stripe resolution failed — never a guessed number).
 */
export type PriceIdentity = 'observed' | 'scenario' | 'unavailable';

export type ValueBasis = 'first' | 'ltv12';

export interface CalculatorOffer {
  id: string;
  label: string;
  family: OfferFamily;
  /** Nominal charge amount; null ONLY when priceState === 'unavailable'. */
  price: number | null;
  priceState: PriceIdentity;
  /** Free-trial length in days; > 0 marks a trial offer (value = conv × price). */
  trialDays?: number;
  isSubscription: boolean;
  /** Provider price id — the join key to served per-product facts (Phase 1 checks lane). */
  stripePriceId?: string;
}

export interface ChainState {
  /** Cost per start, $. */
  cps: number;
  /** First → second screen. */
  p1: number;
  /** Second screen → paywall. */
  p2: number;
  /** Paywall → pressed buy (A2). */
  p3: number;
}

export interface OfferMechanicConfig {
  enabled: boolean;
  /** Offer id from the palette used as the downsell/upsell product. */
  offerId: string | null;
  /** Acceptance conversion of the mechanic's pool. */
  conv: number;
}

/**
 * What an accepted upsell DOES (ruling R-22, 2026-09-22):
 * - `addon`   — one extra payment of the target's price on top of the plan (R2a);
 * - `upgrade` — the buyer switches: the plan's renewals stop and the target's
 *               are counted instead (R2b).
 * Read by the product-shaped `evaluate` only; the shipped offer-keyed half
 * never looks at it.
 */
export type UpsellKind = 'addon' | 'upgrade';

/** The upsell's config: the shared three decisions plus its kind (absent = `addon`). */
export interface UpsellMechanicConfig extends OfferMechanicConfig {
  kind?: UpsellKind;
}

export interface OfferMechanics {
  downsell: OfferMechanicConfig;
  upsell: UpsellMechanicConfig;
}

export interface MechanicsState {
  /** Global gates — a mechanic appears per offer only when its gate is on (AC-008). */
  downsellGate: boolean;
  upsellGate: boolean;
  perOffer: Record<string, OfferMechanics>;
}

/** The whole editable state — snapshot/restore of this object IS the reset (AC-011). */
export interface CalculatorModelState {
  chain: ChainState;
  basis: ValueBasis;
  /** Offer ids currently on the paywall menu. */
  menu: string[];
  /** Solo anchor (paid|pressed) per offer id. */
  solos: Record<string, number>;
  /** Checks per subscriber per offer id (D-axis). */
  checks: Record<string, number>;
  /** Trial → paid conversion per trial offer id. */
  trialConv: Record<string, number>;
  mechanics: MechanicsState;
  trafficPerDay: number;
}

// ---------------------------------------------------------------------------
// Benchmark-book v1 defaults (OUR editorial priors, class `scenario`; plan §6.2)
// ---------------------------------------------------------------------------

/** Same-family nests substitute harder than cross-family (choice model). */
export const NEST_LAMBDA = 0.6;

export const BENCHMARK_CHAIN: ChainState = { cps: 1.5, p1: 0.3, p2: 0.5, p3: 0.35 };

export const BENCHMARK_TRAFFIC_PER_DAY = 1000;

/** Solo-anchor priors (paid|pressed) when nothing is observed yet. */
export const SOLO_PRIOR_BY_FAMILY: Record<OfferFamily, number> = {
  monthly: 0.3,
  annual: 0.2,
  weekly: 0.12,
  daily: 0.1,
  one_time: 0.25,
};

/** Trial offers press-convert better than plain annual (benchmark prior). */
export const TRIAL_SOLO_PRIOR = 0.35;

export const TRIAL_CONV_PRIOR = 0.5;

/** Extra days after the fill date before a trial-bearing look is decidable. */
export const TRIAL_MATURITY_SLACK_DAYS = 3;

export const MECHANIC_CONV_DEFAULTS = { downsell: 0.15, upsell: 0.1 } as const;

// ---------------------------------------------------------------------------
// B — nested-logit menu distribution over PRESSED users
// ---------------------------------------------------------------------------

/** Shared with `solve.ts` — the solvers must clamp exactly as the forward model does. */
export const clampSolo = (value: number): number => Math.min(Math.max(value, 0.005), 0.95);

/**
 * Distribute pressed users over the menu offers + the outside no-buy option.
 * Nests = offer families; λ < 1 makes same-family offers closer substitutes.
 * Σ takes + no-buy = 1 by construction (AC-006/035). Deterministic — pure math.
 */
export function menuTakes(
  menu: string[],
  offers: Record<string, CalculatorOffer>,
  solos: Record<string, number>,
): Record<string, number> {
  const eligible = menu.filter(id => offers[id] && offers[id].price !== null);
  if (eligible.length === 0) return {};
  const families = new Map<OfferFamily, string[]>();
  eligible.forEach(id => {
    const family = offers[id].family;
    families.set(family, [...(families.get(family) ?? []), id]);
  });
  const utility = (id: string): number => {
    const solo = clampSolo(solos[id] ?? 0.005);
    return Math.log(solo / (1 - solo));
  };
  let denominator = 1; // exp(0) — the outside no-buy option
  const familySums = new Map<OfferFamily, { expSum: number; inclusive: number }>();
  families.forEach((members, family) => {
    const expSum = members.reduce((sum, id) => sum + Math.exp(utility(id) / NEST_LAMBDA), 0);
    const inclusive = Math.exp(NEST_LAMBDA * Math.log(expSum));
    familySums.set(family, { expSum, inclusive });
    denominator += inclusive;
  });
  const takes: Record<string, number> = {};
  families.forEach((members, family) => {
    const { expSum, inclusive } = familySums.get(family)!;
    const familyShare = inclusive / denominator;
    members.forEach(id => {
      takes[id] = familyShare * (Math.exp(utility(id) / NEST_LAMBDA) / expSum);
    });
  });
  return takes;
}

export const sumTakes = (takes: Record<string, number>): number =>
  Object.values(takes).reduce((sum, take) => sum + take, 0);

// ---------------------------------------------------------------------------
// D — offer value per accepted user
// ---------------------------------------------------------------------------

/**
 * $ a single accepted user is worth on the given basis. Trial offers:
 * conv × price (× checks on the 12-month basis). `unavailable` prices → 0
 * (the UI keeps such offers out of the menu; typed absence, no guessing).
 */
export function offerValue(
  offer: CalculatorOffer,
  basis: ValueBasis,
  checks: Record<string, number>,
  trialConv: Record<string, number>,
): number {
  if (offer.price === null) return 0;
  const offerChecks = checks[offer.id] ?? CHECK_DEFAULT_BY_FAMILY[offer.family];
  if ((offer.trialDays ?? 0) > 0) {
    const conv = trialConv[offer.id] ?? TRIAL_CONV_PRIOR;
    return conv * offer.price * (basis === 'ltv12' ? offerChecks : 1);
  }
  if (!offer.isSubscription || basis === 'first') return offer.price;
  return offer.price * offerChecks;
}

// ---------------------------------------------------------------------------
// Mechanics — downsell (pool = pressed − paid) and upsell (pool = buyers)
// ---------------------------------------------------------------------------

export interface MechanicsExtras {
  /** Extra $ per pressed user. */
  valueAdd: number;
  /** Extra buyers as a share of pressed users (downsell only — upsell adds a check). */
  buyersAdd: number;
}

export function mechanicsExtras(
  state: CalculatorModelState,
  offers: Record<string, CalculatorOffer>,
  takes: Record<string, number>,
): MechanicsExtras {
  const extras: MechanicsExtras = { valueAdd: 0, buyersAdd: 0 };
  const total = sumTakes(takes);
  const valueOf = (id: string): number =>
    offers[id] ? offerValue(offers[id], state.basis, state.checks, state.trialConv) : 0;
  state.menu.forEach(id => {
    const config = state.mechanics.perOffer[id];
    if (!config) return;
    const take = takes[id] ?? 0;
    if (state.mechanics.downsellGate && config.downsell.enabled && config.downsell.offerId) {
      // Decliners attributed by mix share (approximation until E7 pools are served).
      const pool = total > 0 ? (1 - total) * (take / total) : 0;
      const accepted = pool * config.downsell.conv;
      extras.valueAdd += accepted * valueOf(config.downsell.offerId);
      extras.buyersAdd += accepted;
    }
    if (state.mechanics.upsellGate && config.upsell.enabled && config.upsell.offerId) {
      const accepted = take * config.upsell.conv;
      extras.valueAdd += accepted * valueOf(config.upsell.offerId);
    }
  });
  return extras;
}

// ---------------------------------------------------------------------------
// Chain identity and inverse break-even readings (AC-004/005)
// ---------------------------------------------------------------------------

/** $ the menu (incl. mechanics) returns per pressed user. */
export function valuePerPressed(
  state: CalculatorModelState,
  offers: Record<string, CalculatorOffer>,
): number {
  const takes = menuTakes(state.menu, offers, state.solos);
  const base = Object.entries(takes).reduce(
    (sum, [id, take]) => sum + take * offerValue(offers[id], state.basis, state.checks, state.trialConv),
    0,
  );
  return base + mechanicsExtras(state, offers, takes).valueAdd;
}

/** The menu must return at least this per pressed user for profit ≥ 0. */
export const requiredValuePerPressed = (chain: ChainState): number =>
  chain.cps / (chain.p1 * chain.p2 * chain.p3);

/** Chain identity: profit/start = V·p1·p2·p3 − cps (AC-004). */
export const profitPerStart = (chain: ChainState, valuePressed: number): number =>
  valuePressed * chain.p1 * chain.p2 * chain.p3 - chain.cps;

export type ChainKnob = keyof ChainState;

/**
 * Inverse reading per chain knob: the value at which profit crosses 0 holding
 * the others. cps reads ≤ (cost knob), rates read ≥ (AC-005).
 */
export function chainBreakEven(chain: ChainState, valuePressed: number, knob: ChainKnob): number {
  const { cps, p1, p2, p3 } = chain;
  switch (knob) {
    case 'cps': return valuePressed * p1 * p2 * p3;
    case 'p1': return cps / (valuePressed * p2 * p3);
    case 'p2': return cps / (valuePressed * p1 * p3);
    case 'p3': return cps / (valuePressed * p1 * p2);
    default: return NaN;
  }
}

/**
 * Checks value for `offerId` at which the WHOLE chain breaks even (linear solve
 * on the two-point slope — V is linear in checks_i for fixed takes).
 * null = the knob does not move V (offer not in menu, or first-check basis).
 */
export function checksBreakEven(
  state: CalculatorModelState,
  offers: Record<string, CalculatorOffer>,
  offerId: string,
): number | null {
  const at = (value: number): number =>
    valuePerPressed({ ...state, checks: { ...state.checks, [offerId]: value } }, offers);
  const v1 = at(1);
  const slope = at(2) - v1;
  if (slope <= 0.001) return null;
  return 1 + (requiredValuePerPressed(state.chain) - v1) / slope;
}

/**
 * Mechanic conversion at which the whole chain breaks even. null = no effect;
 * ≤ 0 = the chain is already profitable without it.
 */
export function mechanicBreakEven(
  state: CalculatorModelState,
  offers: Record<string, CalculatorOffer>,
  offerId: string,
  kind: 'downsell' | 'upsell',
): number | null {
  const withConv = (conv: number): CalculatorModelState => ({
    ...state,
    mechanics: {
      ...state.mechanics,
      downsellGate: kind === 'downsell' ? true : state.mechanics.downsellGate,
      upsellGate: kind === 'upsell' ? true : state.mechanics.upsellGate,
      perOffer: {
        ...state.mechanics.perOffer,
        [offerId]: {
          ...state.mechanics.perOffer[offerId],
          [kind]: { ...state.mechanics.perOffer[offerId]?.[kind], enabled: true, conv },
        },
      },
    },
  });
  const v0 = valuePerPressed(withConv(0), offers);
  const slope = valuePerPressed(withConv(1), offers) - v0;
  if (slope <= 0.001) return null;
  return (requiredValuePerPressed(state.chain) - v0) / slope;
}

// ---------------------------------------------------------------------------
// Cannibalization readout (menu with an offer vs without it)
// ---------------------------------------------------------------------------

export interface OfferIncrement {
  /** Δ of Σ takes when the offer is present (percentage points as a fraction). */
  deltaSum: number;
  /** What the offer eats from each neighbour (positive = stolen share). */
  eaten: Array<{ id: string; delta: number }>;
}

export function offerIncrement(
  state: CalculatorModelState,
  offers: Record<string, CalculatorOffer>,
  offerId: string,
): OfferIncrement {
  const withOffer = menuTakes(state.menu, offers, state.solos);
  const without = menuTakes(state.menu.filter(id => id !== offerId), offers, state.solos);
  const deltaSum = sumTakes(withOffer) - sumTakes(without);
  const eaten = state.menu
    .filter(id => id !== offerId)
    .map(id => ({ id, delta: (without[id] ?? 0) - (withOffer[id] ?? 0) }))
    .filter(entry => entry.delta > 0.001);
  return { deltaSum, eaten };
}

// ---------------------------------------------------------------------------
// E6-lite experiment quote (AC-010): two-proportion n/arm, α .05, power .8
// ---------------------------------------------------------------------------

export type ExperimentQuote =
  | { kind: 'no_test'; deltaPp: number }
  | {
      kind: 'quote';
      deltaPp: number;
      nPerArm: number;
      days: number;
      /** $ the project spends anyway over `days` — labeled `estimated` in the UI. */
      calendarSpend: number;
      lookDay: number;
      trialMaturityDays: number;
      pressedPerDay: number;
      spendPerDay: number;
    };

/** Two-proportion sample size per arm (z 1.96 / 0.8416 — α .05 two-sided, power .8). */
export function twoProportionNPerArm(pA: number, pB: number): number {
  const zAlpha = 1.96;
  const zBeta = 0.8416;
  const pooled = (pA + pB) / 2;
  const numerator =
    (zAlpha * Math.sqrt(2 * pooled * (1 - pooled)) +
      zBeta * Math.sqrt(pA * (1 - pA) + pB * (1 - pB))) ** 2;
  return numerator / (pA - pB) ** 2;
}

export function experimentQuote(
  state: CalculatorModelState,
  offers: Record<string, CalculatorOffer>,
  baselineB: number,
): ExperimentQuote {
  const currentB = sumTakes(menuTakes(state.menu, offers, state.solos));
  const deltaPp = (currentB - baselineB) * 100;
  if (Math.abs(currentB - baselineB) < 0.005) return { kind: 'no_test', deltaPp };
  const { chain, trafficPerDay } = state;
  const pressedPerDay = trafficPerDay * chain.p1 * chain.p2 * chain.p3;
  const spendPerDay = trafficPerDay * chain.cps;
  const nPerArm = twoProportionNPerArm(baselineB, currentB);
  const days = nPerArm / (pressedPerDay / 2);
  const maxTrialDays = state.menu.reduce(
    (max, id) => Math.max(max, offers[id]?.trialDays ?? 0),
    0,
  );
  const trialMaturityDays = maxTrialDays > 0 ? maxTrialDays + TRIAL_MATURITY_SLACK_DAYS : 0;
  return {
    kind: 'quote',
    deltaPp,
    nPerArm: Math.ceil(nPerArm),
    days,
    calendarSpend: days * spendPerDay,
    lookDay: Math.ceil(days + trialMaturityDays),
    trialMaturityDays,
    pressedPerDay,
    spendPerDay,
  };
}

// ---------------------------------------------------------------------------
// Empty-registry ladder derivation (AC-003): one anchor → scenario ladder
// ---------------------------------------------------------------------------

export const DEFAULT_MONTHLY_ANCHOR = 19.99;

/** Round to the nearest x.99 (price-ladder convention), floor $0.99. */
export const round99 = (value: number): number => Math.max(0.99, Math.round(value) - 0.01);

export interface DerivedLadder {
  offers: Record<string, CalculatorOffer>;
  /** Suggested starting menu (anchor + annual). */
  menu: string[];
  solos: Record<string, number>;
}

/**
 * Derive the scenario price ladder from ONE monthly anchor:
 * annual = 12 × anchor × 50% off, weekly = ×0.35, premium = ×1.75 — all .99-rounded,
 * every derived price chipped `scenario` (AC-003).
 */
export function deriveLadderFromAnchor(monthlyPrice: number): DerivedLadder {
  const anchor = Number.isFinite(monthlyPrice) && monthlyPrice > 0 ? monthlyPrice : DEFAULT_MONTHLY_ANCHOR;
  const offers: Record<string, CalculatorOffer> = {
    'ladder-monthly': {
      id: 'ladder-monthly',
      label: `Monthly $${anchor.toFixed(2)}`,
      family: 'monthly',
      price: anchor,
      priceState: 'scenario',
      isSubscription: true,
    },
    'ladder-annual': {
      id: 'ladder-annual',
      label: `Annual $${round99(anchor * 12 * 0.5).toFixed(2)}`,
      family: 'annual',
      price: round99(anchor * 12 * 0.5),
      priceState: 'scenario',
      isSubscription: true,
    },
    'ladder-weekly': {
      id: 'ladder-weekly',
      label: `Weekly $${round99(anchor * 0.35).toFixed(2)}`,
      family: 'weekly',
      price: round99(anchor * 0.35),
      priceState: 'scenario',
      isSubscription: true,
    },
    'ladder-premium': {
      id: 'ladder-premium',
      label: `Monthly premium $${round99(anchor * 1.75).toFixed(2)}`,
      family: 'monthly',
      price: round99(anchor * 1.75),
      priceState: 'scenario',
      isSubscription: true,
    },
  };
  const solos: Record<string, number> = {
    'ladder-monthly': SOLO_PRIOR_BY_FAMILY.monthly,
    'ladder-annual': SOLO_PRIOR_BY_FAMILY.annual,
    'ladder-weekly': SOLO_PRIOR_BY_FAMILY.weekly,
    'ladder-premium': SOLO_PRIOR_BY_FAMILY.monthly,
  };
  return { offers, menu: ['ladder-monthly', 'ladder-annual'], solos };
}

// ---------------------------------------------------------------------------
// State factory + mechanics defaults
// ---------------------------------------------------------------------------

export function defaultSoloFor(offer: CalculatorOffer): number {
  if ((offer.trialDays ?? 0) > 0) return TRIAL_SOLO_PRIOR;
  return SOLO_PRIOR_BY_FAMILY[offer.family];
}

/** Nearest price BELOW the offer (fallback: cheapest other) — the natural downsell. */
export function pickDownsellCandidate(
  offerId: string,
  offers: Record<string, CalculatorOffer>,
): string | null {
  const own = offers[offerId]?.price ?? Infinity;
  const others = Object.values(offers).filter(offer => offer.id !== offerId && offer.price !== null);
  const below = others
    .filter(offer => (offer.price ?? 0) < own)
    .sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
  if (below[0]) return below[0].id;
  const cheapest = [...others].sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
  return cheapest[0]?.id ?? null;
}

/** Nearest price ABOVE the offer (fallback: priciest other) — the natural upsell. */
export function pickUpsellCandidate(
  offerId: string,
  offers: Record<string, CalculatorOffer>,
): string | null {
  const own = offers[offerId]?.price ?? 0;
  const others = Object.values(offers).filter(offer => offer.id !== offerId && offer.price !== null);
  const above = others
    .filter(offer => (offer.price ?? 0) > own)
    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
  if (above[0]) return above[0].id;
  const priciest = [...others].sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
  return priciest[0]?.id ?? null;
}

export function freshOfferMechanics(
  offerId: string,
  offers: Record<string, CalculatorOffer>,
): OfferMechanics {
  return {
    downsell: { enabled: false, offerId: pickDownsellCandidate(offerId, offers), conv: MECHANIC_CONV_DEFAULTS.downsell },
    upsell: { enabled: false, offerId: pickUpsellCandidate(offerId, offers), conv: MECHANIC_CONV_DEFAULTS.upsell },
  };
}

/** Build the initial model state for a palette; the returned object doubles as the reset snapshot. */
export function buildInitialState(
  offers: Record<string, CalculatorOffer>,
  menu: string[],
  solos?: Record<string, number>,
): CalculatorModelState {
  const soloMap: Record<string, number> = {};
  const checks: Record<string, number> = {};
  const trialConv: Record<string, number> = {};
  const perOffer: Record<string, OfferMechanics> = {};
  Object.values(offers).forEach(offer => {
    soloMap[offer.id] = solos?.[offer.id] ?? defaultSoloFor(offer);
    checks[offer.id] = CHECK_DEFAULT_BY_FAMILY[offer.family];
    if ((offer.trialDays ?? 0) > 0) trialConv[offer.id] = TRIAL_CONV_PRIOR;
    perOffer[offer.id] = freshOfferMechanics(offer.id, offers);
  });
  return {
    chain: { ...BENCHMARK_CHAIN },
    basis: 'first',
    menu: [...menu],
    solos: soloMap,
    checks,
    trialConv,
    mechanics: { downsellGate: false, upsellGate: false, perOffer },
    trafficPerDay: BENCHMARK_TRAFFIC_PER_DAY,
  };
}

/** Deep-copy a model state (reset snapshots, AC-011). */
export const cloneState = (state: CalculatorModelState): CalculatorModelState =>
  JSON.parse(JSON.stringify(state)) as CalculatorModelState;

// ===========================================================================
// Product-shaped model — ADDITIVE half (owner rulings R1/R2, 2026-09-17)
// ===========================================================================
//
// Everything above is the frozen shipped model, keyed on `CalculatorOffer`.
// Everything below reads the canonical product shape (`UnitProduct`) instead
// and answers the two questions the shipped half cannot:
//
//   R1 — «closing the payment» is a PER-PRODUCT rate living inside the product,
//        not a global stage: choose_i → close_i → paid_i. `menuTakes()` output is
//        therefore read as the CHOICE distribution. Benchmark solo anchors are
//        measured paid|pressed, so `close` defaults to 1.00 and the product-shaped
//        model reproduces the shipped numbers exactly until the knob is moved.
//   R2 — the upsell has two kinds (ruling R-22, 2026-09-22, splitting the
//        2026-09-17 R2):
//        R2a add-on (the default) — a PURCHASE, not a subscription: it adds one
//            nominal payment, never the target's 12-month checks, and never a payer;
//        R2b upgrade — the buyer SWITCHES plans: for the accepted share the base
//            plan's figures leave and the target is counted in their place, read
//            without a trial, as an immediate payer on its own charges count.
//
// Names are `product`-prefixed wherever a shipped export already owns the plain
// name. Nothing here rewrites the shipped half.

/** Per-product payment completion; 1.00 reproduces the shipped model exactly. */
export const CLOSE_DEFAULT = 1.0;

/** The whole editable state of the product-shaped model. */
export interface UnitEconomicsState {
  /** Product ids currently on the paywall. */
  menu: string[];
  /**
   * Of everyone who tapped Buy, the share that picks THIS product — the
   * reader's own number, not an anchor the model has to be solved for.
   *
   * Until 2026-09-18 this was `solos`, a per-product attractiveness the page
   * steered indirectly: the reader asked for a split, a solver moved the
   * anchors until the nested logit produced it, and the no-buy option of that
   * distribution put a hard ceiling of ~95% on "everyone who tapped bought".
   * The owner's model is the plain one — you say how many of a hundred buy each
   * product, and whatever is left bought nothing — so the distribution IS the
   * state. `menuTakes` still exists, untouched, as the thing that PROPOSES a
   * split (the admin pricing calculator reads it and must not move).
   *
   * Σ over the menu is at most 1; the remainder is the people who tapped and
   * picked nothing.
   */
  takes: Record<string, number>;
  /** Payments counted in twelve months, per product id. */
  checks: Record<string, number>;
  /** Trial → paid conversion per trial-bearing product id. */
  trialConv: Record<string, number>;
  /** Payment completion per product id (R1); missing = `CLOSE_DEFAULT`. */
  close: Record<string, number>;
  basis: ValueBasis;
  chain: ChainState;
  mechanics: MechanicsState;
  /** What does not reach the account (2026-09-21). Absent = nothing deducted, every figure gross. */
  deductions?: Deductions;
  /**
   * A product's OWN refund share (P-11), keyed by product id like `close` and
   * `checks`. A product without an entry inherits `deductions.refunds.share`.
   */
  refundShare?: Record<string, number>;
}

/** What each product's mechanics did in one evaluation pass. */
export interface MechanicOutcome {
  downPool: number;
  downAccepted: number;
  downValue: number;
  upPool: number;
  upAccepted: number;
  upValue: number;
}

/** Every quantity a surface may print — no surface invents its own arithmetic. */
export interface UnitEconomicsEvaluation {
  /** Share of pressed users choosing each product. */
  choose: Record<string, number>;
  chooseTotal: number;
  /** Pressed users who picked nothing. */
  noChoice: number;
  /** Chose AND completed the payment. */
  paid: Record<string, number>;
  /** Chose and fell out at the checkout. */
  failed: Record<string, number>;
  paidTotal: number;
  /** Tapped buy and walked away with a purchase (menu + rescued by a downsell). */
  boughtTotal: number;
  rescued: number;
  /** Users whose card is actually charged — a free trial only counts on conversion. */
  payers: number;
  /** $ per pressed user, mechanics included. */
  value: number;
  upsellValue: number;
  downsellValue: number;
  per: Record<string, MechanicOutcome>;
  /** $ per pressed user after every stated deduction; equals `value` when none is stated. */
  valueNet: number;
  deducted: DeductedTotals;
  /**
   * Charges per pressed user over TWELVE months, whatever the value basis, and
   * blended over activation like the money (final fix wave I4) — the
   * denominator of the dispute exposure.
   */
  chargesPerPressed: number;
  /**
   * True once anything is stated — a deduction, or a product's own refund
   * rate that this pass actually reads (`ownRefundRatesCounted`, P-11, M10).
   */
  netBasis: boolean;
  /** What one paying customer of a product gets back, per product id (the Refunded row prints it). */
  refundPerPayer: Record<string, number>;
  /**
   * What each user-named fee (`deductions.custom`, Task 8b) took, per pressed
   * user — in the SAME list order the reader named them. `feeOnCharge` already
   * sums every custom fee into `deducted.fee` alongside the provider's; this is
   * that same total split back out by name, so
   * `Σ customFees[i].perPressed + (the provider's own part) === deducted.fee`.
   */
  customFees: Array<{ id: string; name: string; perPressed: number }>;
}

/**
 * The shipped `CalculatorOffer` view of a product. The nested-logit distribution
 * is frozen shipped code, so the product-shaped menu reuses it through this
 * adapter instead of carrying a second copy of the same formula.
 */
const offerViewOf = (product: UnitProduct): CalculatorOffer => ({
  id: product.id,
  label: product.name,
  family: familyOf(product),
  price: product.price,
  priceState: 'scenario',
  trialDays: trialDaysOf(product),
  isSubscription: product.type === 'subscription',
});

const offerViewsOf = (products: Record<string, UnitProduct>): Record<string, CalculatorOffer> => {
  const views: Record<string, CalculatorOffer> = {};
  Object.values(products).forEach(product => {
    views[product.id] = offerViewOf(product);
  });
  return views;
};

/** `menuTakes` over products — the CHOICE distribution (R1), same math, same numbers. */
export function productMenuTakes(
  menu: string[],
  products: Record<string, UnitProduct>,
  solos: Record<string, number>,
): Record<string, number> {
  return menuTakes(menu, offerViewsOf(products), solos);
}

/**
 * $ a single accepted user is worth on the chosen basis.
 * - no trial  → the price, × payments counted on the 12-month basis
 * - FREE trial → nothing is collected up front: only the converters ever pay
 * - PAID trial → the intro price is charged at the tap (one charge covers the
 *   whole intro duration), then the converters pay the regular price
 */
export function productValue(
  product: UnitProduct,
  basis: ValueBasis,
  checks: Record<string, number>,
  trialConv: Record<string, number>,
): number {
  return productValueWith(product, basis, checks, trialConv, TRIAL_CONV_PRIOR, (p, n) => p * n);
}

/**
 * Payments actually collected per accepted user. A free trial only produces a
 * payer when it converts; a paid trial takes money at the tap, so its buyer is a
 * payer immediately — as is a plain purchase.
 */
export const payFactor = (product: UnitProduct, trialConv: Record<string, number>): number => {
  if (trialOf(product).kind !== 'free') return 1;
  return trialConv[product.id] ?? TRIAL_CONV_PRIOR;
};

/**
 * One pass over the whole model. Returns every quantity the UI prints, so no
 * surface can invent its own arithmetic (the shipped admin app recomputes CAC
 * from accepted users, which over-counts trial starters as payers — UE-AC-026).
 */
/**
 * The dictated distribution, made safe: no negative share, and a total that
 * cannot exceed the hundred people it describes.
 *
 * The widget's controls already cap themselves against the room left, so this
 * is the model refusing to print an impossible world rather than a place where
 * numbers are quietly changed: a document that arrives over-subscribed is
 * scaled, not trusted.
 */
export function normalizeTakes(
  menu: string[],
  takes: Record<string, number>,
): Record<string, number> {
  const safe: Record<string, number> = {};
  let total = 0;
  menu.forEach(id => {
    const take = Math.max(takes[id] ?? 0, 0);
    safe[id] = take;
    total += take;
  });
  if (total <= 1) return safe;
  menu.forEach(id => {
    safe[id] = safe[id] / total;
  });
  return safe;
}

/**
 * The products whose OWN refund rate (P-11) `evaluate` actually reads, in the
 * order the rates were set: every product on the paywall, plus the product a
 * live downsell or a live upgrade lands on (an add-on has no refund pool of its
 * own). A rate left on a product that is off the paywall and no mechanic's
 * target is stated, but not counted — it must neither put the page on the net
 * basis nor print «net of refunds» (final fix wave M10). The ONE rule the
 * evaluation's `netBasis`, the tiles' basis clause and the fold's pointer all
 * read.
 */
export function ownRefundRatesCounted(
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
): string[] {
  const read = new Set<string>();
  state.menu.forEach(id => {
    read.add(id);
    const config = state.mechanics.perOffer[id];
    if (!config) return;
    if (state.mechanics.downsellGate && config.downsell.enabled && config.downsell.offerId) {
      read.add(config.downsell.offerId);
    }
    if (state.mechanics.upsellGate && config.upsell.enabled && config.upsell.offerId && config.upsell.kind === 'upgrade') {
      read.add(config.upsell.offerId);
    }
  });
  return Object.keys(state.refundShare ?? {}).filter(id => read.has(id) && products[id] !== undefined);
}

export function evaluate(
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
): UnitEconomicsEvaluation {
  const choose = normalizeTakes(state.menu, state.takes);
  const chooseTotal = sumTakes(choose);
  const noChoice = 1 - chooseTotal;
  const closeOf = (id: string): number => state.close[id] ?? CLOSE_DEFAULT;
  const deductions = state.deductions ?? emptyDeductions();
  const read = (id: string, perCharge: ChargeReader): number =>
    products[id] ? productValueWith(products[id], state.basis, state.checks, state.trialConv, TRIAL_CONV_PRIOR, perCharge) : 0;
  const valueOf = (id: string): number => read(id, (p, n) => p * n);
  const exTaxOf = (id: string): number => read(id, (p, n) => exTaxAmount(p, deductions.tax) * n);
  const netOf = (id: string): number => read(id, (p, n, y) => netCharges(p, n, deductions, y));
  // Final fix wave I4 (2026-09-22): the dispute exposure's denominator. A
  // dispute rate is a fact about CHARGES, so the count reads the product's
  // twelve months whatever the value basis, and blends activation exactly as
  // the money does — openers pay `n`, non-openers `min(n, nonOpenerChecks)`.
  // With activation unset it is today's count (the seed's 0.8783).
  const chargesOfProduct = (product: UnitProduct): number => {
    const count = (cap?: number): number =>
      productValueWith(product, 'ltv12', state.checks, state.trialConv, TRIAL_CONV_PRIOR, (_, n) => n, cap);
    const activated = deductions.activation;
    return activated === null
      ? count()
      : activated.share * count() + (1 - activated.share) * count(activated.nonOpenerChecks);
  };
  const chargesOf = (id: string): number => (products[id] ? chargesOfProduct(products[id]) : 0);
  const payersOf = (id: string): number => (products[id] ? payFactor(products[id], state.trialConv) : 1);
  /**
   * D3 (2026-09-22 provider-scheme plan) — a refund or a dispute follows the
   * provider's OWN scheme (Task 3, `refundLossOnCharge`/`disputeLossOnCharge`
   * in `./deductions`, shared with `payback.ts` so the payback curve can
   * never disagree with this evaluation, UE-AC-109): a store gives its cut
   * back on a refund (`onRefund: 'returned'`) and treats a dispute exactly
   * like a refund (`onDispute: 'refund'`, no per-event fee on top of the
   * loss); a processor keeps its cut through a refund and a dispute costs
   * the charge plus a flat fee (`PROCESSOR_SCHEME` — today's N0 arithmetic,
   * unchanged). The `*OfProduct` pair reads a product object directly — the
   * upgrade path (R2b, below) hands over a synthetic target that is never in
   * `products`.
   */
  const refundLossOfProduct = (product: UnitProduct): number => refundLossOnCharge(firstChargeOf(product), deductions);
  const disputeLossOfProduct = (product: UnitProduct): number => disputeLossOnCharge(firstChargeOf(product), deductions);
  const refundLossOf = (id: string): number => (products[id] ? refundLossOfProduct(products[id]) : 0);
  const disputeLossOf = (id: string): number => (products[id] ? disputeLossOfProduct(products[id]) : 0);
  // Task 14 — activation: how much of `netOf`'s own figure a non-opener's
  // fewer charges cost, on the SAME basis `netOf` already reads (net once
  // fee/tax are stated, gross otherwise — `netCharge` degrades on its own).
  // `null` activation costs nothing. Kept apart from `netOf` itself so
  // `deducted.fee`/`deducted.tax` (built on the UNBLENDED figure below) never
  // pick up activation's own loss — each deduction stays its own row.
  const activation = deductions.activation;
  const activationLossOf = (id: string): number => {
    if (activation === null || !products[id]) return 0;
    const full = netOf(id);
    const capped = productValueWith(
      products[id],
      state.basis,
      state.checks,
      state.trialConv,
      TRIAL_CONV_PRIOR,
      (p, n, y) => netCharges(p, n, deductions, y),
      activation.nonOpenerChecks,
    );
    return (1 - activation.share) * (full - capped);
  };
  // P-11: a product's own refund rate wins; without one it inherits the fold's general rate.
  const refundShareOf = (id: string): number => state.refundShare?.[id] ?? deductions.refunds?.share ?? 0;
  // A user-named fee's own cut of one charge (Task 8b) — the same shape
  // `feeOnCharge` sums into `netCharge` for the provider's fee, kept apart so
  // each fee can be read back by name.
  const customFeeCharge = (fee: CustomFee, price: number): number =>
    fee.pct * chargedAmount(price, deductions.tax) + fee.fixed;
  const customFeeOf = (id: string, fee: CustomFee): number => read(id, (p, n) => customFeeCharge(fee, p) * n);
  // R2b — the SAME readings of a product handed over as an object rather than
  // by id, because an upgrade's target is read WITHOUT its trial (an upgrade is
  // a charge now, not a new trial). Its id is kept, so its charges count is
  // still `state.checks[target]` or its family default, and its refund share
  // is still its own (`refundShareOf(target.id)`).
  const readProduct = (product: UnitProduct, perCharge: ChargeReader, checksOverride?: number): number =>
    productValueWith(product, state.basis, state.checks, state.trialConv, TRIAL_CONV_PRIOR, perCharge, checksOverride);
  const activationLossOfProduct = (product: UnitProduct): number => {
    if (activation === null) return 0;
    const full = readProduct(product, (p, n, y) => netCharges(p, n, deductions, y));
    const capped = readProduct(product, (p, n, y) => netCharges(p, n, deductions, y), activation.nonOpenerChecks);
    return (1 - activation.share) * (full - capped);
  };

  const paid: Record<string, number> = {};
  const failed: Record<string, number> = {};
  state.menu.forEach(id => {
    const chosen = choose[id] ?? 0;
    paid[id] = chosen * closeOf(id);
    failed[id] = chosen * (1 - closeOf(id));
  });
  const paidTotal = state.menu.reduce((sum, id) => sum + paid[id], 0);

  let value = state.menu.reduce((sum, id) => sum + paid[id] * valueOf(id), 0);
  let valueExTax = state.menu.reduce((sum, id) => sum + paid[id] * exTaxOf(id), 0);
  let valueNet = state.menu.reduce((sum, id) => sum + paid[id] * netOf(id), 0);
  let chargesPerPressed = state.menu.reduce((sum, id) => sum + paid[id] * chargesOf(id), 0);
  let payers = state.menu.reduce((sum, id) => sum + paid[id] * payersOf(id), 0);
  // Refunds and disputes are PER PAYER: the share of the cards charged that come back.
  let refundsLoss = state.menu.reduce((sum, id) =>
    sum + paid[id] * payersOf(id) * refundShareOf(id) * refundLossOf(id), 0);
  let disputesLoss = state.menu.reduce((sum, id) =>
    sum + paid[id] * payersOf(id) * (deductions.disputes?.share ?? 0) * disputeLossOf(id), 0);
  // Task 14 — activation's own loss, per pressed user, weighted the SAME way
  // `netOf` itself is (paid[id], never payersOf(id) — the free-trial
  // conversion is already inside `netOf`/`activationLossOf` via `productValueWith`).
  let activationLoss = state.menu.reduce((sum, id) => sum + paid[id] * activationLossOf(id), 0);
  // Every user-named fee (Task 8b), accumulated the same way `deducted.fee`
  // is — one entry per fee, in the order the reader named them, so
  // Σ customFeeTotals[i] + the provider's own part === deducted.fee.
  const customFeeTotals: Record<string, number> = {};
  deductions.custom.forEach(fee => {
    customFeeTotals[fee.id] = state.menu.reduce((sum, id) => sum + paid[id] * customFeeOf(id, fee), 0);
  });
  let rescued = 0;
  let upsellValue = 0;
  let downsellValue = 0;
  const per: Record<string, MechanicOutcome> = {};

  state.menu.forEach(id => {
    const config = state.mechanics.perOffer[id];
    // Both pools are a property of the MENU, not of a mechanic: they are well
    // defined for every product on the paywall whether or not one is configured.
    //   Downsell pool — everyone who tapped buy on this product and did not end
    //     up paying: its own failed checkouts PLUS its share of the people who
    //     picked nothing. At close = 1 this is exactly the shipped mix-share formula.
    //   Upsell pool — the people who actually paid for this product.
    // Only the accepted/value parts below need a configured mechanic.
    const pool = failed[id] + (chooseTotal > 0 ? noChoice * ((choose[id] ?? 0) / chooseTotal) : 0);
    per[id] = {
      downPool: pool,
      downAccepted: 0,
      downValue: 0,
      upPool: paid[id],
      upAccepted: 0,
      upValue: 0,
    };
    if (!config) return;
    if (state.mechanics.downsellGate && config.downsell.enabled && config.downsell.offerId) {
      const targetId = config.downsell.offerId;
      const accepted = pool * config.downsell.conv;
      const added = accepted * valueOf(targetId);
      per[id].downAccepted = accepted;
      per[id].downValue = added;
      value += added;
      valueExTax += accepted * exTaxOf(targetId);
      valueNet += accepted * netOf(targetId);
      chargesPerPressed += accepted * chargesOf(targetId);
      downsellValue += added;
      rescued += accepted;
      payers += accepted * payersOf(targetId);
      refundsLoss += accepted * payersOf(targetId) * refundShareOf(targetId) * refundLossOf(targetId);
      disputesLoss += accepted * payersOf(targetId) * (deductions.disputes?.share ?? 0) * disputeLossOf(targetId);
      activationLoss += accepted * activationLossOf(targetId);
      deductions.custom.forEach(fee => {
        customFeeTotals[fee.id] += accepted * customFeeOf(targetId, fee);
      });
    }
    // R2b — an UPGRADE replaces the base plan for the people who take it
    // (ruling R-22). Every per-person figure the base contributed through the
    // menu sums above is taken back out for them — value, ex-tax, net, charges,
    // payers, refunds, disputes, activation, each custom fee — and the target
    // is counted in its place: read without a trial, one payer per accepted
    // person, on its own charges count, refund share, disputes, activation and
    // custom fees. The base's first charge is treated as credited into the
    // upgrade (not counted separately) — the conservative reading. `upValue`
    // carries the NET change, which is negative when the target is worth less
    // over the basis than the plan it replaces; nothing clamps it.
    // A target that is gone changes nothing: the base stays as it was.
    const upsellTargetId =
      state.mechanics.upsellGate && config.upsell.enabled ? config.upsell.offerId : null;
    if (upsellTargetId && config.upsell.kind === 'upgrade') {
      const accepted = paid[id] * config.upsell.conv;
      per[id].upAccepted = accepted;
      const source = products[upsellTargetId];
      if (source && products[id]) {
        const target: UnitProduct = { ...source, trial: undefined };
        const disputeShare = deductions.disputes?.share ?? 0;
        const change = accepted * (readProduct(target, (p, n) => p * n) - valueOf(id));
        per[id].upValue = change;
        value += change;
        valueExTax += accepted * (readProduct(target, (p, n) => exTaxAmount(p, deductions.tax) * n) - exTaxOf(id));
        valueNet += accepted * (readProduct(target, (p, n, y) => netCharges(p, n, deductions, y)) - netOf(id));
        chargesPerPressed += accepted * (chargesOfProduct(target) - chargesOf(id));
        // The upgrader is ONE payer of the target instead of `payersOf(base)`
        // of the base — a free-trial base's unconverted starters become payers.
        payers += accepted * (1 - payersOf(id));
        refundsLoss += accepted * (
          refundShareOf(target.id) * refundLossOfProduct(target) - payersOf(id) * refundShareOf(id) * refundLossOf(id)
        );
        disputesLoss += accepted * disputeShare * (
          disputeLossOfProduct(target) - payersOf(id) * disputeLossOf(id)
        );
        activationLoss += accepted * (activationLossOfProduct(target) - activationLossOf(id));
        deductions.custom.forEach(fee => {
          customFeeTotals[fee.id] += accepted * (
            readProduct(target, (p, n) => customFeeCharge(fee, p) * n) - customFeeOf(id, fee)
          );
        });
        upsellValue += change;
      }
    } else if (upsellTargetId) {
      // R2a — an ADD-ON (the default: `kind` absent or 'addon') is a one-off
      // purchase: one nominal payment added, no new payer, therefore no refund
      // or dispute pool of its own. Unchanged since 2026-09-17.
      const target = products[upsellTargetId];
      const accepted = paid[id] * config.upsell.conv;
      const added = accepted * (target ? target.price : 0);
      per[id].upAccepted = accepted;
      per[id].upValue = added;
      value += added;
      valueExTax += accepted * (target ? exTaxAmount(target.price, deductions.tax) : 0);
      valueNet += accepted * (target ? netCharge(target.price, deductions) : 0);
      chargesPerPressed += accepted;
      upsellValue += added;
      deductions.custom.forEach(fee => {
        customFeeTotals[fee.id] += accepted * (target ? customFeeCharge(fee, target.price) : 0);
      });
    }
  });

  const netBeforeLosses = valueNet;
  valueNet -= refundsLoss + disputesLoss + activationLoss;

  return {
    choose,
    chooseTotal,
    noChoice,
    paid,
    failed,
    paidTotal,
    boughtTotal: paidTotal + rescued,
    rescued,
    payers,
    value,
    upsellValue,
    downsellValue,
    per,
    valueNet,
    deducted: {
      tax: value - valueExTax,
      fee: valueExTax - netBeforeLosses,
      refunds: refundsLoss,
      disputes: disputesLoss,
      activation: activationLoss,
    },
    chargesPerPressed,
    netBasis: hasDeductions(deductions) || ownRefundRatesCounted(state, products).length > 0,
    refundPerPayer: Object.fromEntries(state.menu.map(id => [id, refundShareOf(id) * refundLossOf(id)])),
    customFees: deductions.custom.map(fee => ({ id: fee.id, name: fee.name, perPressed: customFeeTotals[fee.id] })),
  };
}

// ---------------------------------------------------------------------------
// Product-shaped inverse readings — every one is a two-point linear solve
// (V is linear in checks_i, in close_i and in each mechanic's conversion);
// the one exception is the charges count under activation, which is linear in
// two pieces (see `productChecksBreakEven`).
// ---------------------------------------------------------------------------

/**
 * The knob value at which `basisValue` reaches `requiredValue`, for a knob the
 * value is linear in — the break-even's own two-point solve at any target
 * (goal seek, `goal.ts`, 2026-10-09). The break-even is this solve at
 * `requiredValuePerPressed(chain)`, so a goal of ROAS 1.00× and the chain
 * cards can never disagree. `null` = the knob does not move the value.
 */
export const productKnobForValue = (
  products: Record<string, UnitProduct>,
  mutate: (x: number) => UnitEconomicsState,
  requiredValue: number,
): number | null => {
  const at = (x: number): number => basisValue(evaluate(mutate(x), products));
  const v0 = at(0);
  const slope = at(1) - v0;
  if (slope <= 0.001) return null;
  return (requiredValue - v0) / slope;
};

const solveProductBreakEven = (
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
  mutate: (x: number) => UnitEconomicsState,
): number | null => productKnobForValue(products, mutate, requiredValuePerPressed(state.chain));

/**
 * Payments counted for `productId` at which the whole chain breaks even.
 *
 * The value is linear in the count — until activation is stated (Task 14):
 * non-openers stop at `nonOpenerChecks`, so above that count only the openers'
 * share of each extra charge is left and the line has ONE kink there. A
 * straight line through 1 and 2 charges then over-states every charge past the
 * kink (final fix wave I3: activation 30 %, non-openers 1.5, $1.60 per start
 * read 3.26 while the chain only clears from 4.74). So the solve is two exact
 * pieces: the line from 1 to the kink, and the line past it. Without activation
 * (or with the kink at one charge) it is today's two-point solve, unchanged.
 *
 * `null` = the knob does not move the value at all (off the paywall, the
 * first-charge basis, no payers). `+Infinity` = it moves it, but past the kink
 * nothing is left to move and the chain still does not clear.
 */
export function productChecksBreakEven(
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
  productId: string,
): number | null {
  return productChecksForValue(state, products, productId, requiredValuePerPressed(state.chain));
}

/**
 * Payments counted for `productId` at which `basisValue` reaches
 * `requiredValue` — `productChecksBreakEven`'s two exact pieces at any target
 * (goal seek, 2026-10-09). Same `null` / `+Infinity` contract.
 */
export function productChecksForValue(
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
  productId: string,
  requiredValue: number,
): number | null {
  const at = (value: number): number =>
    basisValue(evaluate({ ...state, checks: { ...state.checks, [productId]: value } }, products));
  const required = requiredValue;
  const v1 = at(1);
  const kink = state.deductions?.activation?.nonOpenerChecks ?? 1;
  if (kink <= 1) {
    const slope = at(2) - v1;
    if (slope <= 0.001) return null;
    return 1 + (required - v1) / slope;
  }
  const vKink = at(kink);
  const below = (vKink - v1) / (kink - 1);
  if (below <= 0.001) return null;
  if (required <= vKink) return 1 + (required - v1) / below;
  const above = at(kink + 1) - vKink;
  if (above <= 0.001) return Number.POSITIVE_INFINITY;
  return kink + (required - vKink) / above;
}

/** Payment completion for `productId` at which the whole chain breaks even (R1). */
export const closeBreakEven = (
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
  productId: string,
): number | null =>
  solveProductBreakEven(state, products, x => ({
    ...state,
    close: { ...state.close, [productId]: x },
  }));

/** Trial → paid conversion for `productId` at which the whole chain breaks even. */
export const trialConvBreakEven = (
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
  productId: string,
): number | null =>
  solveProductBreakEven(state, products, x => ({
    ...state,
    trialConv: { ...state.trialConv, [productId]: x },
  }));

/** Mechanic conversion at which the whole chain breaks even. null = no effect. */
export const productMechanicBreakEven = (
  state: UnitEconomicsState,
  products: Record<string, UnitProduct>,
  productId: string,
  kind: 'downsell' | 'upsell',
): number | null =>
  solveProductBreakEven(state, products, conv => ({
    ...state,
    mechanics: {
      ...state.mechanics,
      downsellGate: kind === 'downsell' ? true : state.mechanics.downsellGate,
      upsellGate: kind === 'upsell' ? true : state.mechanics.upsellGate,
      perOffer: {
        ...state.mechanics.perOffer,
        [productId]: {
          ...state.mechanics.perOffer[productId],
          [kind]: { ...state.mechanics.perOffer[productId]?.[kind], enabled: true, conv },
        },
      },
    },
  }));

/**
 * What the choice model would do with a hundred buy-taps.
 *
 * The anchors are the benchmark book's own priors per product unless the
 * caller has better ones, so the proposal is an editorial scenario like every
 * other default on the page: close substitutes take from each other harder
 * than distant ones, and the people the menu does not convince stay in the
 * no-buy remainder. It is offered to the reader, never imposed — the state is
 * whatever they settle on.
 */
export function proposeTakes(
  menu: string[],
  products: Record<string, UnitProduct>,
  solos?: Record<string, number>,
): Record<string, number> {
  return productMenuTakes(menu, products, modelSolos(menu, products, solos));
}

/** The anchors a proposal runs on: the caller's, else each product's book prior. */
export function modelSolos(
  menu: string[],
  products: Record<string, UnitProduct>,
  solos?: Record<string, number>,
): Record<string, number> {
  const anchors: Record<string, number> = {};
  menu.forEach(id => {
    const product = products[id];
    if (!product) return;
    anchors[id] = solos?.[id] ?? productDefaultSoloFor(product);
  });
  return anchors;
}

/**
 * What the MODEL says a product costs the ones beside it — the paywall with it
 * against the paywall without it.
 *
 * It takes the anchors explicitly now: the calculator's state carries the
 * reader's own split, and this reading belongs to the proposal path
 * (`proposeTakes`), which is the only place the nested logit still speaks.
 */
export function productOfferIncrement(
  menu: string[],
  products: Record<string, UnitProduct>,
  solos: Record<string, number>,
  productId: string,
): OfferIncrement {
  const withProduct = productMenuTakes(menu, products, solos);
  const without = productMenuTakes(menu.filter(id => id !== productId), products, solos);
  const deltaSum = sumTakes(withProduct) - sumTakes(without);
  const eaten = menu
    .filter(id => id !== productId)
    .map(id => ({ id, delta: (without[id] ?? 0) - (withProduct[id] ?? 0) }))
    .filter(entry => entry.delta > 0.001);
  return { deltaSum, eaten };
}

// ---------------------------------------------------------------------------
// Product-shaped palette defaults
// ---------------------------------------------------------------------------

/**
 * Written out rather than delegated to the shipped `defaultSoloFor`: that one
 * keys on `trialDays > 0`, which cannot tell a PAID trial from a free one, and a
 * paid trial is still a trial-bearing offer for the solo prior (R6).
 */
export const productDefaultSoloFor = (product: UnitProduct): number =>
  trialOf(product).kind !== 'none' ? TRIAL_SOLO_PRIOR : SOLO_PRIOR_BY_FAMILY[familyOf(product)];

/*
 * The three pickers below are the SHIPPED ones read through the offer adapter —
 * the same mechanism `productMenuTakes` uses. A product palette has nothing to
 * say about tie-breaks or fallbacks that the shipped picker does not already
 * say, so a second copy would only be a place for the two to drift apart.
 */

/**
 * Nearest price BELOW the product (fallback: cheapest other) over the WHOLE
 * palette — a mechanic may point at a product deliberately kept off the paywall.
 */
export const productPickDownsellCandidate = (
  productId: string,
  products: Record<string, UnitProduct>,
): string | null => pickDownsellCandidate(productId, offerViewsOf(products));

/** Nearest price ABOVE the product (fallback: priciest other) over the whole palette. */
export const productPickUpsellCandidate = (
  productId: string,
  products: Record<string, UnitProduct>,
): string | null => pickUpsellCandidate(productId, offerViewsOf(products));

/** Both mechanics off, each pre-aimed at its natural neighbour in the palette. */
export const productFreshOfferMechanics = (
  productId: string,
  products: Record<string, UnitProduct>,
): OfferMechanics => freshOfferMechanics(productId, offerViewsOf(products));

// ---------------------------------------------------------------------------
// Formatting — typed absence prints `n/a`, NEVER `$0.00` (global constraint)
// ---------------------------------------------------------------------------

/**
 * `$12.34` / `$1,459.70` / `−$12.34` / `n/a`.
 *
 * Grouped, always. A four-digit amount without a thousands separator is the
 * classic misread — `$3536.07` scanned as `$353.61` — and this formatter is the
 * ONE money format the product-shaped surfaces print, so the separator has to
 * live here rather than in each caller (design review P1-11, 2026-09-17).
 * `en-US` is named explicitly and never the runtime's locale: the page states
 * prices in dollars, and a browser set to `de-DE` would otherwise print
 * `$1.459,70` beside a `$19.99` typed into a field.
 */
export const usd = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return 'n/a';
  const amount = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${value < 0 ? '−$' : '$'}${amount}`;
};

/** One decimal place: `40.4%` / `n/a`. */
export const pct1 = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return 'n/a';
  return `${(value * 100).toFixed(1)}%`;
};

/** Whole percent: `40%` / `n/a`. */
export const pct0 = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return 'n/a';
  return `${Math.round(value * 100)}%`;
};

/** Signed percentage points: `+2.5pp` / `−2.5pp` / `n/a`. */
export const pp1 = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return 'n/a';
  return `${value >= 0 ? '+' : '−'}${Math.abs(value * 100).toFixed(1)}pp`;
};
